// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AngleRight from "@flip/XDL/Icons/AngleRight";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { getSystemColor, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface TitleTypographyProps {
    link?: string | LinkFieldValue;
    onLinkClick?: () => void;
    linkText?: string;
    openInNewTab?: boolean;
    extendedStyles?: TitleTypographyStyles;
    extraChild?: React.ReactNode;
}

type Props = TitleTypographyProps;

interface TitleTypographyLink {
    link?: LinkFieldValue;
    onClick?: () => void;
    url?: string;
    openInNewTab?: boolean;
    linkText?: string;
    styles: TitleTypographyStyles;
    pageTitle?: string;
}

export interface TitleTypographyStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
}

const titleTypographyStyles: TitleTypographyStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: baseline;
            flex-wrap: wrap;
            width: 100%;
            margin-bottom: 24px;
        `,
    },
    title: {
        variant: "h2",
        color: "text.main",
        css: css`
            margin-right: 32px;
            margin-bottom: 0;
        `,
    },
    link: {
        color: "text.link",
        hoverStyle: {
            color: getSystemColor("common.ui8"),
        },
        icon: {
            iconProps: {
                src: AngleRight,
                size: 12,
                color: "text.link",
            },
            position: "right",
        },
        typographyProps: {
            size: 16,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        css: css`
            margin-bottom: 6px;
            align-items: center;
            ${IconWrapper} {
                margin-left: 7px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const TitleLink: FC<TitleTypographyLink> = ({ url, pageTitle, openInNewTab, linkText, styles, onClick }) => {
    return (
        <Link
            href={url}
            onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                e.stopPropagation();
                onClick?.();
            }}
            target={openInNewTab ? "_blank" : undefined}
            {...styles.link}
            aria-label={`${linkText} ${pageTitle}`}
        >
            {linkText}
        </Link>
    );
};

const TitleTypographyLink: FC<TitleTypographyLink> = ({ link, ...otherProps }) => {
    const { url, title } = useGetLink(link ?? { type: "Page", value: "" });
    return <TitleLink {...otherProps} url={url} pageTitle={title} />;
};

const TitleTypography: FC<Props> = ({
    openInNewTab,
    linkText,
    extendedStyles,
    children,
    link,
    onLinkClick,
    extraChild,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(titleTypographyStyles, extendedStyles));
    const linkProps = {
        styles,
        openInNewTab,
        linkText,
    };

    const linkComponent = () => {
        if ((!link && !onLinkClick) || !linkText) {
            return null;
        }
        if (typeof link === "string") {
            return <TitleLink url={link} {...linkProps} />;
        }
        if (link && link.value) {
            return <TitleTypographyLink link={link} {...linkProps} />;
        }
        if (onLinkClick) {
            return <TitleLink onClick={onLinkClick} {...linkProps} />;
        }
        return null;
    };

    const linkElement = linkComponent();
    if (!React.Children.count(children) && !linkElement) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...otherProps}>
            <Typography {...styles.title}>{children}</Typography>
            {linkElement}
            {extraChild}
        </StyledWrapper>
    );
};

export default TitleTypography;
